import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, RefreshCw } from "lucide-react";

import { QueueItemPresenceAvatars } from "@/components/QueueItemPresenceAvatars";
import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useQueueItemPresence } from "@/lib/useQueueItemPresence";

import { NewManufacturerInvoiceModal } from "./NewManufacturerInvoiceModal";
import { formatManufacturerInvoiceDate } from "./suntronicInvoiceDate";
import {
  fetchSuntronicInvoiceOptions,
  type SuntronicInvoiceOptionRow,
} from "./suntronicInvoicesApi";

const PAGE_HELP = [
  "Manufacturer invoices from contract manufacturers (currently Suntronic).",
  "Open an invoice to match finished goods and link Megaventory GIPI documents.",
  "Reconciled invoices are hidden unless you turn that filter on.",
].join(" ");

const SUNTRONIC_QUERY_ROOT = ["accounting", "suntronic-invoices"] as const;

type InvoiceSortKey = "number" | "manufacturer" | "date" | "total" | "status";

function parseTotalCost(value: string | null | undefined): number {
  if (value == null || value.trim() === "") return 0;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function invoiceWorkPath(invoiceId: number): string {
  return `/accounting/suntronic-invoices/${encodeURIComponent(String(invoiceId))}`;
}

export function SuntronicInvoicesPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showReconciled, setShowReconciled] = useState(false);
  const [newInvoiceOpen, setNewInvoiceOpen] = useState(false);
  const [sortKey, setSortKey] = useState<InvoiceSortKey>("number");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  const listQuery = useQuery({
    queryKey: [...SUNTRONIC_QUERY_ROOT, "invoice-options", showReconciled],
    queryFn: () => fetchSuntronicInvoiceOptions(showReconciled),
  });

  const toggleSort = (key: InvoiceSortKey): void => {
    if (sortKey === key) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "total" || key === "date" || key === "number" ? "desc" : "asc");
  };

  const filteredRows = useMemo(() => {
    const rows = listQuery.data ?? [];
    const needle = search.trim().toLowerCase();
    if (needle === "") {
      return rows;
    }
    return rows.filter((row) =>
      [row.number, row.manufacturer, row.date, row.total, row.reconciled ? "reconciled" : "open"]
        .map((value) => String(value ?? "").toLowerCase())
        .some((value) => value.includes(needle)),
    );
  }, [listQuery.data, search]);

  const filtersActive = search.trim() !== "" || showReconciled;

  const { viewersForEntity } = useQueueItemPresence({
    pathname: "/accounting/suntronic-invoices",
    entityType: "manufacturer_invoice",
  });

  const columns = useMemo((): HubDataTableColumn<SuntronicInvoiceOptionRow, InvoiceSortKey>[] => {
    return [
      {
        id: "number",
        header: "Invoice",
        sortKey: "number",
        sortValue: (row) => row.number,
        cell: (row) => (
          <div className="flex items-center gap-2">
            <Link className="font-medium text-sky-700 hover:underline" to={invoiceWorkPath(row.value)}>
              {row.number}
            </Link>
            <QueueItemPresenceAvatars
              people={viewersForEntity(String(row.value))}
              labelPrefix="Viewing this invoice"
            />
          </div>
        ),
      },
      {
        id: "manufacturer",
        header: "Manufacturer",
        sortKey: "manufacturer",
        sortValue: (row) => row.manufacturer,
        cell: (row) => row.manufacturer,
      },
      {
        id: "date",
        header: "Date",
        sortKey: "date",
        overflow: "nowrap",
        sortValue: (row) => row.date ?? "",
        cell: (row) => formatManufacturerInvoiceDate(row.date),
      },
      {
        id: "total",
        header: "Total",
        sortKey: "total",
        align: "right",
        overflow: "nowrap",
        sortValue: (row) => parseTotalCost(row.total_cost_raw),
        cell: (row) => row.total,
      },
      {
        id: "status",
        header: "Status",
        sortKey: "status",
        sortValue: (row) => Number(row.reconciled),
        cell: (row) => (
          <Badge tone={row.reconciled ? "success" : "warning"}>{row.reconciled ? "Reconciled" : "Open"}</Badge>
        ),
      },
    ];
  }, [viewersForEntity]);

  const title = listQuery.isLoading
    ? "Loading manufacturer invoices…"
    : `${filteredRows.length} manufacturer invoice${filteredRows.length === 1 ? "" : "s"}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Manufacturer invoices</PageTitleWithHelp>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => void listQuery.refetch()}
            disabled={listQuery.isFetching}
          >
            {listQuery.isFetching ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Refresh
          </Button>
          <Button type="button" size="sm" onClick={() => setNewInvoiceOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New invoice
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <div>
            <Label htmlFor="manufacturer-invoice-search">Search invoices</Label>
            <Input
              id="manufacturer-invoice-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by invoice number, manufacturer, or date..."
              className="mt-1.5"
            />
          </div>
          <div className="flex items-end">
            <label className="flex h-10 items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={showReconciled}
                onChange={(event) => setShowReconciled(event.target.checked)}
              />
              Show reconciled invoices
            </label>
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        title={title}
        columns={columns}
        rows={filteredRows}
        rowKey={(row) => row.value}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        loading={listQuery.isLoading}
        loadingMessage="Loading manufacturer invoices…"
        error={listQuery.isError ? "Could not load manufacturer invoices." : null}
        empty="No open manufacturer invoices."
        isFiltered={filtersActive}
        filteredEmpty="No manufacturer invoices match these filters."
      />

      {newInvoiceOpen ? (
        <NewManufacturerInvoiceModal
          onClose={() => setNewInvoiceOpen(false)}
          onCreated={async (invoiceId) => {
            setNewInvoiceOpen(false);
            await queryClient.invalidateQueries({ queryKey: [...SUNTRONIC_QUERY_ROOT, "invoice-options"] });
            navigate(invoiceWorkPath(invoiceId));
          }}
        />
      ) : null}
    </div>
  );
}
